import React, { useMemo, useState } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastProvider';
import { getParticipantsForOrg, getSubOrgsForOrg, getGroupsForOrg, getSubGroupsForOrg } from '../schemas/mockData';
import { UsersIcon, UserPlusIcon, AlertTriangleIcon, BuildingIcon, LayersIcon, MoreVerticalIcon, SidebarIcon } from 'lucide-react';
import { HierarchyVisualizationPanel } from '../components/HierarchyVisualizationPanel';
import { Pagination } from '../components/Pagination';
import { SearchInput } from '../components/SearchInput';
export function ParticipantManagement() {
  const {
    language,
    currentOrganization,
    getHierarchyLabels,
    t
  } = useOrganization();
  const {
    showToast
  } = useToast();
  const {
    isAdmin
  } = useAuth();
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15; // Optimized for 1440px screens
  // Get organization-specific data
  const MOCK_PARTICIPANTS = useMemo(() => getParticipantsForOrg(currentOrganization.id), [currentOrganization.id]);
  const MOCK_SUB_ORGS = useMemo(() => getSubOrgsForOrg(currentOrganization.id), [currentOrganization.id]);
  const MOCK_GROUPS = useMemo(() => getGroupsForOrg(currentOrganization.id), [currentOrganization.id]);
  const MOCK_SUBGROUPS = useMemo(() => getSubGroupsForOrg(currentOrganization.id), [currentOrganization.id]);
  const isK12 = currentOrganization.orgType === 'K12';
  const [view, setView] = useState<'participants' | 'groups' | 'suborgs'>('participants');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubOrg, setSelectedSubOrg] = useState<string>('all');
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const [showAtRiskOnly, setShowAtRiskOnly] = useState(false);
  const [showHierarchyPanel, setShowHierarchyPanel] = useState(true);
  const [selectedHierarchyNode, setSelectedHierarchyNode] = useState<string | undefined>(undefined);
  const [selectedHierarchyType, setSelectedHierarchyType] = useState<string | undefined>(undefined);
  const hierarchyLabels = getHierarchyLabels();
  // Calculate participant counts for hierarchy
  const participantCounts = useMemo(() => {
    const counts: {
      [key: string]: number;
    } = {};
    MOCK_SUB_ORGS.forEach(subOrg => {
      counts[subOrg.id] = MOCK_PARTICIPANTS.filter(p => p.subOrgId === subOrg.id).length;
    });
    MOCK_GROUPS.forEach(group => {
      counts[group.id] = MOCK_PARTICIPANTS.filter(p => p.groupId === group.id).length;
    });
    MOCK_SUBGROUPS.forEach(subGroup => {
      counts[subGroup.id] = MOCK_PARTICIPANTS.filter(p => p.subGroupId === subGroup.id).length;
    });
    return counts;
  }, [MOCK_PARTICIPANTS, MOCK_SUB_ORGS, MOCK_GROUPS, MOCK_SUBGROUPS]);
  const handleHierarchyNodeSelect = (nodeId: string, nodeType: string) => {
    setSelectedHierarchyNode(nodeId);
    setSelectedHierarchyType(nodeType);
    setCurrentPage(1); // Reset to first page on filter change
    if (nodeType === 'organization') {
      setSelectedSubOrg('all');
      setSelectedGroup('all');
    } else if (nodeType === 'sub-org') {
      setSelectedSubOrg(nodeId);
      setSelectedGroup('all');
    } else if (nodeType === 'group') {
      setSelectedGroup(nodeId);
      const group = MOCK_GROUPS.find(g => g.id === nodeId);
      if (group?.subOrgId) {
        setSelectedSubOrg(group.subOrgId);
      }
    } else if (nodeType === 'sub-group') {
      const subGroup = MOCK_SUBGROUPS.find(sg => sg.id === nodeId);
      if (subGroup) {
        setSelectedGroup(subGroup.groupId);
        const group = MOCK_GROUPS.find(g => g.id === subGroup.groupId);
        if (group?.subOrgId) {
          setSelectedSubOrg(group.subOrgId);
        }
      }
    }
  };
  const filteredParticipants = useMemo(() => {
    const filtered = MOCK_PARTICIPANTS.filter(p => {
      if (showAtRiskOnly && !p.atRisk) return false;
      if (selectedHierarchyType === 'sub-org' && p.subOrgId !== selectedHierarchyNode) {
        return false;
      }
      if (selectedHierarchyType === 'group' && p.groupId !== selectedHierarchyNode) {
        return false;
      }
      if (selectedHierarchyType === 'sub-group' && p.subGroupId !== selectedHierarchyNode) {
        return false;
      }
      if (!selectedHierarchyNode) {
        if (selectedSubOrg !== 'all' && p.subOrgId !== selectedSubOrg) {
          return false;
        }
        if (selectedGroup !== 'all' && p.groupId !== selectedGroup) {
          return false;
        }
      }
      if (searchQuery) {
        const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
        const query = searchQuery.toLowerCase();
        if (!fullName.includes(query) && !p.externalUserId.toLowerCase().includes(query)) {
          return false;
        }
      }
      return true;
    });
    return filtered;
  }, [MOCK_PARTICIPANTS, selectedHierarchyNode, selectedHierarchyType, selectedSubOrg, selectedGroup, showAtRiskOnly, searchQuery, MOCK_GROUPS, MOCK_SUBGROUPS]);
  // Pagination
  const totalPages = Math.ceil(filteredParticipants.length / itemsPerPage);
  const paginatedParticipants = filteredParticipants.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  return <div className="h-full flex bg-gray-50" data-id="element-1986">
      {/* Hierarchy Panel - Fixed positioning with proper z-index */}
      {showHierarchyPanel && <div className="w-80 flex-shrink-0 border-r border-gray-200 bg-white fixed left-64 top-16 bottom-0 overflow-y-auto z-20" data-id="element-1987">
          <HierarchyVisualizationPanel subOrgs={MOCK_SUB_ORGS} groups={MOCK_GROUPS} subGroups={MOCK_SUBGROUPS} participantCounts={participantCounts} selectedNodeId={selectedHierarchyNode} onNodeSelect={handleHierarchyNodeSelect} data-id="element-1988" />
        </div>}
      {/* Main Content - Adjusted margin to prevent overlap */}
      <div className="flex-1 flex flex-col overflow-hidden" style={{
      marginLeft: showHierarchyPanel ? '320px' : '0'
    }} data-id="element-1989">
        {/* Page Header - Increased padding and proper spacing */}
        <div className="page-header" data-id="element-1990">
          <div className="page-header-content" data-id="element-1991">
            <div className="flex items-center justify-between" data-id="element-1992">
              <div className="flex items-center gap-4" data-id="element-1993">
                <button onClick={() => setShowHierarchyPanel(!showHierarchyPanel)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title={showHierarchyPanel ? t.hideHierarchy : t.showHierarchy} data-id="element-1994">
                  <SidebarIcon className="w-5 h-5 text-gray-600" data-id="element-1995" />
                </button>
                <div data-id="element-1996">
                  <h1 className="text-2xl font-bold text-gray-900" data-id="element-1997">
                    {t.participantManagement}
                  </h1>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-1998">
                    {isK12 ? t.manageParticipantsAcross : t.manageParticipantsTeams}
                  </p>
                </div>
              </div>
              <button className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium shadow-sm hover:shadow flex items-center gap-2" data-id="element-1999">
                <UserPlusIcon className="w-4 h-4" data-id="element-2000" />
                {t.addParticipants}
              </button>
            </div>
          </div>
        </div>
        {/* Stats Bar */}
        <div className="page-stats" data-id="element-2001">
          <div className="page-stats-content" data-id="element-2002">
            <div className="stats-grid" data-id="element-2003">
              <div className="stat-card" data-id="element-2004">
                <div className="stat-card-icon bg-blue-100" data-id="element-2005">
                  <UsersIcon className="w-5 h-5 text-blue-600" data-id="element-2006" />
                </div>
                <div className="stat-card-value" data-id="element-2007">
                  {filteredParticipants.length}
                </div>
                <div className="stat-card-label" data-id="element-2008">
                  {selectedHierarchyNode ? t.filteredParticipants : t.totalParticipants}
                </div>
              </div>
              <div className="stat-card" data-id="element-2009">
                <div className="stat-card-icon bg-purple-100" data-id="element-2010">
                  <LayersIcon className="w-5 h-5 text-purple-600" data-id="element-2011" />
                </div>
                <div className="stat-card-value" data-id="element-2012">{MOCK_GROUPS.length}</div>
                <div className="stat-card-label" data-id="element-2013">
                  {isK12 ? t.gradeGroups : t.teamsClasses}
                </div>
              </div>
              {isK12 && <div className="stat-card" data-id="element-2014">
                  <div className="stat-card-icon bg-green-100" data-id="element-2015">
                    <BuildingIcon className="w-5 h-5 text-green-600" data-id="element-2016" />
                  </div>
                  <div className="stat-card-value" data-id="element-2017">{MOCK_SUB_ORGS.length}</div>
                  <div className="stat-card-label" data-id="element-2018">{t.schools}</div>
                </div>}
              <div className="stat-card" data-id="element-2019">
                <div className="stat-card-icon bg-red-100" data-id="element-2020">
                  <AlertTriangleIcon className="w-5 h-5 text-red-600" data-id="element-2021" />
                </div>
                <div className="stat-card-value" data-id="element-2022">
                  {filteredParticipants.filter(p => p.atRisk).length}
                </div>
                <div className="stat-card-label" data-id="element-2023">{t.atRiskFlagged}</div>
              </div>
            </div>
          </div>
        </div>
        {/* Main Content Area */}
        <div className="page-main" data-id="element-2024">
          <div className="page-main-content" data-id="element-2025">
            <div className="content-card" data-id="element-2026">
              {/* Card Header with Filters */}
              <div className="content-card-header" data-id="element-2027">
                <div className="px-6 py-4 space-y-4" data-id="element-2028">
                  {/* View Tabs */}
                  <div className="flex gap-2" data-id="element-2029">
                    <button onClick={() => setView('participants')} className={`px-4 py-2.5 rounded-lg font-medium transition-all text-sm flex items-center gap-2 ${view === 'participants' ? 'bg-blue-50 text-blue-700 border-2 border-blue-200' : 'text-gray-600 hover:bg-gray-50 border-2 border-transparent'}`} data-id="element-2030">
                      <UsersIcon className="w-4 h-4" data-id="element-2031" />
                      {t.participants}
                    </button>
                    <button onClick={() => setView('groups')} className={`px-4 py-2.5 rounded-lg font-medium transition-all text-sm flex items-center gap-2 ${view === 'groups' ? 'bg-blue-50 text-blue-700 border-2 border-blue-200' : 'text-gray-600 hover:bg-gray-50 border-2 border-transparent'}`} data-id="element-2032">
                      <LayersIcon className="w-4 h-4" data-id="element-2033" />
                      {isK12 ? t.groups : t.teamsClasses}
                    </button>
                    {isK12 && <button onClick={() => setView('suborgs')} className={`px-4 py-2.5 rounded-lg font-medium transition-all text-sm flex items-center gap-2 ${view === 'suborgs' ? 'bg-blue-50 text-blue-700 border-2 border-blue-200' : 'text-gray-600 hover:bg-gray-50 border-2 border-transparent'}`} data-id="element-2034">
                        <BuildingIcon className="w-4 h-4" data-id="element-2035" />
                        {t.schools}
                      </button>}
                  </div>
                  {/* Filters */}
                  {view === 'participants' && <div className="flex items-center gap-3" data-id="element-2036">
                      <div className="flex-1 max-w-md" data-id="element-2037">
                        <SearchInput value={searchQuery} onChange={value => {
                      setSearchQuery(value);
                      setCurrentPage(1);
                    }} placeholder={t.searchParticipants} data-id="element-2038" />
                      </div>
                      <button onClick={() => {
                    setShowAtRiskOnly(!showAtRiskOnly);
                    setCurrentPage(1);
                  }} className={`h-10 px-4 text-sm rounded-lg font-medium transition-all flex items-center gap-2 whitespace-nowrap ${showAtRiskOnly ? 'bg-red-50 text-red-700 border-2 border-red-200' : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300'}`} data-id="element-2039">
                        <AlertTriangleIcon className="w-4 h-4" data-id="element-2040" />
                        {t.atRiskOnly}
                      </button>
                    </div>}
                </div>
              </div>
              {/* Card Body */}
              <div className="content-card-body" data-id="element-2041">
                {view === 'participants' && <>
                    {/* Participants Table */}
                    <div className="table-container" data-id="element-2042">
                      <div className="table-scrollable" data-id="element-2043">
                        <table className="enterprise-table" data-id="element-2044">
                          <thead data-id="element-2045">
                            <tr data-id="element-2046">
                              <th className="cell-left" data-id="element-2047">{t.participant}</th>
                              <th className="cell-left" data-id="element-2048">{t.externalId}</th>
                              <th className="cell-left" data-id="element-2049">{t.group}</th>
                              {isK12 && <th className="cell-left" data-id="element-2050">{t.school}</th>}
                              <th className="cell-center" data-id="element-2051">{t.status}</th>
                              <th className="cell-center" data-id="element-2052">{t.assessments}</th>
                              <th className="cell-right" data-id="element-2053">{t.actions}</th>
                            </tr>
                          </thead>
                          <tbody data-id="element-2054">
                            {paginatedParticipants.map(participant => {
                          const group = MOCK_GROUPS.find(g => g.id === participant.groupId);
                          const subOrg = MOCK_SUB_ORGS.find(s => s.id === participant.subOrgId);
                          return <tr key={participant.id} data-id="element-2055">
                                  <td data-id="element-2056">
                                    <div className="table-user-info" data-id="element-2057">
                                      <div className="table-avatar" data-id="element-2058">
                                        {participant.firstName.charAt(0)}
                                      </div>
                                      <div data-id="element-2059">
                                        <div className="table-user-name" data-id="element-2060">
                                          {participant.firstName}{' '}
                                          {participant.lastName}
                                        </div>
                                        {participant.email && <div className="table-user-meta" data-id="element-2061">
                                            {participant.email}
                                          </div>}
                                      </div>
                                      {participant.atRisk && <span className="table-badge at-risk" data-id="element-2062">
                                          {t.atRiskOnly}
                                        </span>}
                                    </div>
                                  </td>
                                  <td data-id="element-2063">
                                    <span className="table-id" data-id="element-2064">
                                      {participant.externalUserId}
                                    </span>
                                  </td>
                                  <td data-id="element-2065">
                                    <span className="text-sm text-gray-700" data-id="element-2066">
                                      {group?.name || 'N/A'}
                                    </span>
                                  </td>
                                  {isK12 && <td data-id="element-2067">
                                      <span className="text-sm text-gray-700" data-id="element-2068">
                                        {subOrg?.name || 'N/A'}
                                      </span>
                                    </td>}
                                  <td className="cell-center" data-id="element-2069">
                                    <span className={`table-badge ${participant.status === 'active' ? 'status-active' : 'status-inactive'}`} data-id="element-2070">
                                      {participant.status}
                                    </span>
                                  </td>
                                  <td className="cell-center" data-id="element-2071">
                                    <span className="text-sm font-semibold text-gray-900" data-id="element-2072">
                                      {participant.completedAssessments}
                                    </span>
                                  </td>
                                  <td className="cell-right" data-id="element-2073">
                                    <button className="table-action-btn" data-id="element-2074">
                                      <MoreVerticalIcon className="w-4 h-4 text-gray-600" data-id="element-2075" />
                                    </button>
                                  </td>
                                </tr>;
                        })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {/* Pagination */}
                    {filteredParticipants.length > itemsPerPage && <div className="pagination-container" data-id="element-2076">
                        <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={filteredParticipants.length} itemsPerPage={itemsPerPage} onPageChange={setCurrentPage} data-id="element-2077" />
                      </div>}
                    {/* Empty State */}
                    {filteredParticipants.length === 0 && <div className="table-empty-state" data-id="element-2078">
                        <UsersIcon className="table-empty-icon" data-id="element-2079" />
                        <div className="table-empty-title" data-id="element-2080">
                          {t.noParticipantsFound}
                        </div>
                        <div className="table-empty-description" data-id="element-2081">
                          {t.adjustFilters}
                        </div>
                      </div>}
                  </>}
                {/* Groups and Schools views */}
                {view === 'groups' && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2082">
                    {MOCK_GROUPS.map(group => {
                  const subOrg = MOCK_SUB_ORGS.find(s => s.id === group.subOrgId);
                  return <div key={group.id} className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md hover:border-blue-300 transition-all" data-id="element-2083">
                          <div className="flex items-start justify-between mb-3" data-id="element-2084">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-2085">
                              <LayersIcon className="w-5 h-5 text-purple-600" data-id="element-2086" />
                            </div>
                            <button className="table-action-btn" data-id="element-2087">
                              <MoreVerticalIcon className="w-4 h-4 text-gray-600" data-id="element-2088" />
                            </button>
                          </div>
                          <h3 className="text-sm font-bold text-gray-900 mb-2" data-id="element-2089">
                            {group.name}
                          </h3>
                          {group.metadata?.description && <p className="text-xs text-gray-600 mb-3" data-id="element-2090">
                              {group.metadata.description}
                            </p>}
                          <div className="space-y-2 mb-3" data-id="element-2091">
                            <div className="flex items-center justify-between text-xs" data-id="element-2092">
                              <span className="text-gray-600" data-id="element-2093">
                                {t.participants}:
                              </span>
                              <span className="font-semibold text-gray-900" data-id="element-2094">
                                {group.participantCount}
                              </span>
                            </div>
                            {isK12 && subOrg && <div className="flex items-center justify-between text-xs" data-id="element-2095">
                                <span className="text-gray-600" data-id="element-2096">
                                  {t.school}:
                                </span>
                                <span className="font-semibold text-gray-900" data-id="element-2097">
                                  {subOrg.name}
                                </span>
                              </div>}
                          </div>
                          <button className="w-full px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors font-medium text-sm" data-id="element-2098">
                            {t.viewDetails}
                          </button>
                        </div>;
                })}
                  </div>}
                {view === 'suborgs' && isK12 && <div className="space-y-3" data-id="element-2099">
                    {MOCK_SUB_ORGS.map(subOrg => {
                  const groupsInSchool = MOCK_GROUPS.filter(g => g.subOrgId === subOrg.id);
                  return <div key={subOrg.id} className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md hover:border-blue-300 transition-all" data-id="element-2100">
                          <div className="flex items-center justify-between" data-id="element-2101">
                            <div className="flex items-center gap-4 flex-1" data-id="element-2102">
                              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-2103">
                                <BuildingIcon className="w-6 h-6 text-green-600" data-id="element-2104" />
                              </div>
                              <div className="flex-1" data-id="element-2105">
                                <h3 className="text-sm font-bold text-gray-900 mb-1" data-id="element-2106">
                                  {subOrg.name}
                                </h3>
                                <div className="flex gap-4 text-xs text-gray-600" data-id="element-2107">
                                  <span data-id="element-2108">
                                    <span className="font-semibold text-gray-900" data-id="element-2109">
                                      {groupsInSchool.length}
                                    </span>{' '}
                                    {t.groups}
                                  </span>
                                  <span data-id="element-2110">
                                    <span className="font-semibold text-gray-900" data-id="element-2111">
                                      {subOrg.participantCount}
                                    </span>{' '}
                                    {t.participants}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <button className="table-action-btn" data-id="element-2112">
                              <MoreVerticalIcon className="w-4 h-4 text-gray-600" data-id="element-2113" />
                            </button>
                          </div>
                        </div>;
                })}
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}